import {
  ArrowCounterClockwise,
  Copy,
  DotsThreeVertical,
  GithubLogo,
  Info,
  XCircle,
} from '@phosphor-icons/react';
import { IconButton, Menu } from '@fragments-sdk/ui';
import type { FindingDoc } from './findings-types';
import { isFixable } from './findings-display-model';
import {
  useFindingsAction,
  useFindingsRowStatic,
} from './findings-table-context';
import { ActionRail, RowActionButton } from '@/components/table-kit';
import s from './findings.module.scss';

export function FindingActionsCell({ finding }: { finding: FindingDoc }) {
  const {
    canCreateAutofix,
    canCreateIssue,
    canCreateWaiver,
    copy,
    fix,
    ignore,
    readOnly,
    reopen,
    createIssue,
    inspect,
  } = useFindingsRowStatic();
  const { fixLoading, fixResult, issueLoading } = useFindingsAction();
  const fixable = isFixable(finding);
  const canMutate = !readOnly;
  const canFix = canMutate && canCreateAutofix;
  return (
    <ActionRail>
      {canFix && fixable && !fixResult && (
        <RowActionButton
          variant="primary"
          disabled={fixLoading}
          onClick={() => fix(finding._id)}
        >
          Fix
        </RowActionButton>
      )}
      <Menu>
        <Menu.Trigger asChild>
          <IconButton
            size="sm"
            variant="ghost"
            className={s.rowMenuTrigger}
            aria-label="Finding actions"
            title="Finding actions"
          >
            <DotsThreeVertical size={15} weight="bold" />
          </IconButton>
        </Menu.Trigger>
        <Menu.Content align="end" sideOffset={6}>
          <Menu.Item
            icon={<Info size={12} />}
            onSelect={() => inspect(finding)}
          >
            Inspect finding
          </Menu.Item>
          <Menu.Item
            icon={<Copy size={12} />}
            onSelect={() => copy(finding.fingerprint)}
          >
            Copy fingerprint
          </Menu.Item>
          {finding.filePath && (
            <Menu.Item
              icon={<Copy size={12} />}
              onSelect={() => copy(finding.filePath!)}
            >
              Copy path
            </Menu.Item>
          )}
          {finding.githubIssueUrl ? (
            <Menu.Item
              icon={<GithubLogo size={12} />}
              onSelect={() => {
                window.open(
                  finding.githubIssueUrl,
                  '_blank',
                  'noopener,noreferrer',
                );
              }}
            >
              Open issue
            </Menu.Item>
          ) : canMutate && canCreateIssue ? (
            <Menu.Item
              icon={<GithubLogo size={12} />}
              disabled={issueLoading}
              onSelect={() => createIssue(finding)}
            >
              Create issue
            </Menu.Item>
          ) : (
            <Menu.Item icon={<GithubLogo size={12} />} disabled>
              {readOnly ? 'Demo read-only' : 'Upgrade for issues'}
            </Menu.Item>
          )}
          {canMutate &&
            (finding.status === 'open' ? (
              canCreateWaiver ? (
                <Menu.Item
                  icon={<XCircle size={12} />}
                  onSelect={() => ignore(finding)}
                >
                  Ignore
                </Menu.Item>
              ) : (
                <Menu.Item icon={<XCircle size={12} />} disabled>
                  Upgrade for waivers
                </Menu.Item>
              )
            ) : (
              <Menu.Item
                icon={<ArrowCounterClockwise size={12} />}
                onSelect={() => reopen(finding._id)}
              >
                Reopen
              </Menu.Item>
            ))}
        </Menu.Content>
      </Menu>
    </ActionRail>
  );
}
